import { useCallback, useMemo } from 'react';

import { ButtonProps } from '@swyftx/aviary/atoms/Button';
import { marginService } from '@swyftx/margin';
import { MarginOrderDirection, MarginOrderType, ProtectionOrderVariant } from '@swyftx/margin/build/types';
import { calculateMarginRequirements, isInstrumentOpen } from '@swyftx/margin/build/utils';

import { Big } from '@shared/safe-big';

import { DateTime } from 'luxon';
import { useMarginAccount } from 'src/lib/margin/hooks/account';
import { useMarginInstruments } from 'src/lib/margin/hooks/instruments';
import { useMarginPrices } from 'src/lib/margin/hooks/prices';
import { useMarginProtectionOrderError } from 'src/lib/margin/hooks/trade/useMarginProtectionOrderError';
import { useMarginTradePriceError } from 'src/lib/margin/hooks/trade/useMarginTradePriceError';

import { useMarginTradePanelStore } from './useMarginTradePanelStore';
import { MarginTakeProfitStopLossType } from './useMarginTradePanelStore.types';
import { useMarginLotsError } from '../useMarginLotsError';

type Props = {
  tradeSide: MarginOrderDirection;
};

const useMarginTradePanelStoreUtils = ({ tradeSide }: Props) => {
  const {
    tradePrice,
    tradeType,
    takeProfit: tradeTakeProfit,
    stopLoss: tradeStopLoss,
    setTakeProfit,
    setStopLoss,
    setTradePrice,
    tradeLots,
    setTradeLots,
  } = useMarginTradePanelStore();

  const { selectedMarketPrice, getCurrencyRate, isStalePriceData } = useMarginPrices();
  const { selectedMarket } = useMarginInstruments();
  const { availableMargin, currency, account } = useMarginAccount();

  const marketPrice = useMemo(() => {
    if (tradeSide === MarginOrderDirection.Long) {
      return Big(selectedMarketPrice?.ask).toFixed(selectedMarket?.precision) || '';
    } else {
      return Big(selectedMarketPrice?.bid).toFixed(selectedMarket?.precision) || '';
    }
  }, [selectedMarket, selectedMarketPrice, tradeSide]);

  const inverseMarketPrice = useMemo(() => {
    if (tradeSide === MarginOrderDirection.Long) {
      return Big(selectedMarketPrice?.bid).toFixed(selectedMarket?.precision) || '';
    } else {
      return Big(selectedMarketPrice?.ask).toFixed(selectedMarket?.precision) || '';
    }
  }, [selectedMarket, selectedMarketPrice, tradeSide]);

  const price = useMemo(() => {
    if (tradeType === MarginOrderType.Market) {
      return marketPrice;
    }
    return tradePrice[tradeSide];
  }, [marketPrice, tradePrice, tradeSide, tradeType]);

  const inversePrice = useMemo(() => {
    if (tradeType === MarginOrderType.Market) {
      return inverseMarketPrice;
    }

    return tradePrice[tradeSide];
  }, [inverseMarketPrice, tradePrice, tradeSide, tradeType]);

  const audPrice = useMemo(() => {
    if (!price || !selectedMarket || !currency) return Big(0);

    const rate = getCurrencyRate();

    if (!rate) return Big(0);

    return Big(price).times(rate);
  }, [currency, getCurrencyRate, price, selectedMarket]);

  const lots = useMemo(() => tradeLots[tradeSide], [tradeLots, tradeSide]);
  const takeProfit = useMemo(() => tradeTakeProfit[tradeSide], [tradeTakeProfit, tradeSide]);
  const stopLoss = useMemo(() => tradeStopLoss[tradeSide], [tradeStopLoss, tradeSide]);

  const usedMargin = useMemo(
    () =>
      calculateMarginRequirements({
        lots: Number(lots),
        orderPrice: audPrice.toString(),
        contractSize: selectedMarket?.contractSize,
        leverage: selectedMarket?.maxLeverage,
      }),
    [audPrice, lots, selectedMarket?.contractSize, selectedMarket?.maxLeverage],
  );

  const maxMarginPosition = useMemo(
    () =>
      Big(availableMargin)
        .times(selectedMarket?.maxLeverage || 1)
        .div(audPrice),
    [audPrice, availableMargin, selectedMarket?.maxLeverage],
  );

  const maxPosition = useMemo(() => {
    if (!selectedMarket || audPrice.eq(0) || Big(availableMargin).lte(0)) return Big(1);

    return Big(
      marginService.calculateMaxPositionSize({
        availableMargin: Big(availableMargin).toString(),
        marketPrice: audPrice.toString(),
        orderType: tradeType,
        maxMarketLeverage: selectedMarket.maxLeverage,
        marketPrecision: selectedMarket.precision,
        maxMarketLots: Big(selectedMarket.maxLots).toNumber(),
      }),
    );
  }, [audPrice, availableMargin, selectedMarket, tradeType]);

  const { error: priceError } = useMarginTradePriceError({
    price,
    tradeSide,
    tradeType,
    selectedMarketPrice,
    selectedMarket,
  });
  const { error: takeProfitError } = useMarginProtectionOrderError({
    price,
    tradeSide,
    tradeType,
    value: takeProfit.price,
    protectionOrderType: ProtectionOrderVariant.TakeProfit,
    selectedMarket,
  });
  const { error: stopLossError } = useMarginProtectionOrderError({
    price,
    tradeSide,
    tradeType,
    value: stopLoss.price,
    protectionOrderType: ProtectionOrderVariant.StopLoss,
    selectedMarket,
  });
  const { error: lotsError } = useMarginLotsError({ market: selectedMarket, lots, maxMarginPosition });

  const isValidTrade = useMemo(() => {
    if (!account?.active) return false;
    if (!price) return false;
    if (!lots) return false;
    if (!selectedMarket?.open) return false;
    if (!isInstrumentOpen(DateTime.now().setZone('UTC+10'), selectedMarket.times)) return false;

    if (tradeSide === MarginOrderDirection.Long && !selectedMarket?.long) return false;
    if (tradeSide === MarginOrderDirection.Short && !selectedMarket?.short) return false;

    if (priceError !== undefined) return false;
    if (lotsError !== undefined) return false;
    if (takeProfit.checked && (takeProfitError !== undefined || !takeProfit.price)) return false;
    if (stopLoss.checked && (stopLossError !== undefined || !stopLoss.price)) return false;

    return true;
  }, [
    account?.active,
    lots,
    lotsError,
    price,
    priceError,
    selectedMarket?.long,
    selectedMarket?.open,
    selectedMarket?.short,
    selectedMarket?.times,
    stopLoss.checked,
    stopLoss.price,
    stopLossError,
    takeProfit.checked,
    takeProfit.price,
    takeProfitError,
    tradeSide,
  ]);

  const buttonColor = useMemo((): ButtonProps['color'] => {
    if (tradeSide === MarginOrderDirection.Long) return 'success';
    return 'destructive';
  }, [tradeSide]);

  const isMarketOrder = useMemo(() => tradeType === MarginOrderType.Market, [tradeType]);

  const onUpdateTradePrice = useCallback(
    (value: string) => {
      setTradePrice(tradeSide, value);
    },
    [setTradePrice, tradeSide],
  );

  const onUpdateTradeLots = useCallback(
    (value: string) => {
      setTradeLots(tradeSide, value);
    },
    [setTradeLots, tradeSide],
  );

  const onUpdateTakeProfit = useCallback(
    (value: Partial<MarginTakeProfitStopLossType>) => {
      setTakeProfit(tradeSide, value);
    },
    [setTakeProfit, tradeSide],
  );

  const onUpdateStopLoss = useCallback(
    (value: Partial<MarginTakeProfitStopLossType>) => {
      setStopLoss(tradeSide, value);
    },
    [setStopLoss, tradeSide],
  );

  const onResetTradePanel = useCallback(() => {
    onUpdateTradeLots('');
    onUpdateTakeProfit({ checked: false, price: '' });
    onUpdateStopLoss({ checked: false, price: '' });
    onUpdateTradePrice('');
  }, [onUpdateStopLoss, onUpdateTakeProfit, onUpdateTradeLots, onUpdateTradePrice]);

  return {
    price,
    inversePrice,
    audPrice,
    marketPrice,
    inverseMarketPrice,
    lots,
    takeProfit,
    tradeType,
    stopLoss,
    isValidTrade,
    selectedMarket,
    selectedMarketPrice,
    usedMargin,
    availableMargin,
    maxMarginPosition,
    maxPosition,
    buttonColor,
    isMarketOrder,
    onUpdateTradePrice,
    onUpdateTradeLots,
    onUpdateStopLoss,
    onUpdateTakeProfit,
    onResetTradePanel,
    isStalePriceData,
  };
};

export { useMarginTradePanelStoreUtils };
